// api.ts — Call Agent Admin veri katmanı (data layer).
// ─────────────────────────────────────────────────────────────────────────
// TÜM veri erişimi bu modülden geçer. Hiçbir bileşen doğrudan fetch yapmaz.
// Phase 3: gövdeler gerçek REST çağrıları (backend: src/api/*). İmzalar ve
// types.ts'teki şekiller Phase 0'daki mock ile birebir aynı.
//
// Oturum: httpOnly `caa_sid` çerezi (credentials: 'include'). 401 gelirse
// `caa:unauthorized` window olayı yayınlanır — App.tsx oturumu kapatır.

import type {
  OffboardReason,
  OffboardResult,
  AgentCommand,
  CustomerCallsResult,
  ExtHealth,
  ExtFleet,
  ExtReport,
  ExtReportStatus,
  AgentDayDetail,
  AgentRecord,
  AgentWeek,
  AgentReport,
  Alert,
  AlertFilter,
  AlertRuleTitles,
  Brand,
  BrandId,
  BreaksData,
  DarkCompare,
  DarkUpload,
  DateRange,
  Granularity,
  GrantResult,
  Lang,
  LazinessConfig,
  ListDetailData,
  ListDetailPeriod,
  BonusUsageData,
  LiveAgent,
  MutationResult,
  PanelPasswordList,
  PanelPasswordResult,
  PanelSyncResult,
  NewHireInput,
  NewHireResult,
  DarkSecretResult,
  PanelUser,
  ReportPreset,
  Role,
  RosterAgent,
  ScheduleDay,
  ScheduleState,
  SmsOutcome,
  SmsReport,
  StatusDef,
  StatusKey,
  StatusSystems,
  TeamReport,
  Thresholds,
  WeeklyReportData,
  WhatsappReport,
  WinbackReport,
} from './types';

// build.mjs bake eder; "" = aynı origin (prod: API SPA'yı da servis eder).
declare const __API_BASE_URL__: string;
const API_BASE = (typeof __API_BASE_URL__ !== 'undefined' ? __API_BASE_URL__ : '').replace(/\/+$/, '');

export class ApiError extends Error {
  status: number;
  /** 429 yanıtındaki `Retry-About` değil — `Retry-After`, SANİYE cinsinden. Yalnız
   *  sunucu gönderdiyse ve okunabildiyse dolar: yerel geliştirmede CORS başlığı
   *  `Access-Control-Expose-Headers` olmadan gizleyebilir, o zaman undefined kalır
   *  ve çağıran kendi varsayılan bekleme süresine düşer. */
  retryAfter?: number;
  constructor(status: number, message: string, retryAfter?: number) {
    super(message);
    this.status = status;
    this.retryAfter = retryAfter;
  }
}

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(API_BASE + path, { credentials: 'include', ...init });
  } catch {
    throw new ApiError(0, 'network_error');
  }
  if (res.status === 401 && !path.startsWith('/api/auth/login')) {
    try {
      localStorage.removeItem('caa.session');
    } catch {
      /* yoksay */
    }
    window.dispatchEvent(new Event('caa:unauthorized'));
    throw new ApiError(401, 'unauthorized');
  }
  if (!res.ok) {
    let msg = 'request_failed';
    try {
      const body = (await res.json()) as { error?: string };
      if (body && body.error) msg = body.error;
    } catch {
      /* gövde JSON değilse genel mesaj */
    }
    const ra = Number(res.headers.get('Retry-After'));
    throw new ApiError(res.status, msg, Number.isFinite(ra) && ra > 0 ? ra : undefined);
  }
  return (await res.json()) as T;
}

const jsonInit = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

/** Sorgu dizesi üretir. Diziler virgülle birleşir; undefined atlanır.
 *  DİKKAT: boş dizi PARAMETRE OLARAK GİDER ("brands=") — "hiç marka seçili değil"
 *  ile "filtre yok" sunucuda farklı anlamlardır (mock davranışı korunur). */
function qs(params: Record<string, string | number | string[] | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined) continue;
    sp.set(k, Array.isArray(v) ? v.join(',') : String(v));
  }
  const s = sp.toString();
  return s ? '?' + s : '';
}

// ── Statik kataloglar (sunucuya gitmez — constants ile aynı kaynak) ─────────
// /api/brands DİYE BİR UÇ YOK — marka çipleri yalnız bu listeden gelir, o yüzden yeni bir
// markayı buraya eklemeyi unutmak "sunucu döndürmüyor" gibi değil, "marka hiç yok" gibi
// görünür. constants.ts ALL_BRANDS / DEPARTMENTS ile birebir aynı tutulacak ikizler.
// BetBooker RETIRED 2026-09-09 — HIDDEN from the panel, NOT deleted. It is dropped from this chip
// catalog (and its twin ALL_BRANDS in constants.ts) so no chip renders anywhere and the roster tab
// (SCHEDULE_BRANDS-gated on betbooker) disappears with it. Everything else is left intact on
// purpose: the backend still lists+accepts betbooker (ALL_BRAND_KEYS / BRAND_ORDER / VALID_BRANDS),
// the label/tz maps below keep it, its DB rows + proxy + sheet all remain, and ingest stays on — so
// nothing historical is destroyed and un-hiding is just re-adding this entry + its twin + an SPA
// rebuild. (Trade-off accepted: with no chip, even an admin can't select a historical BetBooker
// Reports view from the UI; the data is safe in Postgres.)
const BRANDS: Brand[] = [
  { id: 'vegas', name: 'Vegas' },
  { id: 'prime', name: 'Prime' },
  { id: 'primevip', name: 'Prime VIP' },
  { id: 'hera', name: 'Hera' },
];

const DEPARTMENTS = ['VIP', 'PREVIP', 'VEGAS CALL', 'PRIME CALL', 'BETBOOKER CALL', 'HERA CALL'];

const PALETTE = {
  green: '#37c46b',
  amber: '#e0a23c',
  orange: '#f2784b',
  red: '#ef5d5d',
  pink: '#e0639b',
  grey: '#9aa4b2',
  violet: '#a07fe0',
  bronze: '#b9772a',
  indigo: '#6b7fd7',
};

// Mirrors the engine's STATUS_DEFS.tr (src/engine/status-map.js) AND the extension grid
// (call-agent-extension src/shared/constants.ts) exactly — colors must agree across all three.
const STATUS_TR: StatusDef[] = [
  { key: 'positive', label: 'Olumlu', color: PALETTE.green },
  { key: 'not_interested', label: 'Olumsuz / İlgilenmiyor', color: PALETTE.violet },
  { key: 'hung_up_brand', label: 'Markayı Duyunca Kapattı', color: PALETTE.pink },
  { key: 'call_back', label: 'Meşgul / Tekrar Aranacak', color: PALETTE.orange },
  { key: 'no_answer', label: 'Ulaşılamadı / Yanıt Yok', color: PALETTE.amber },
  { key: 'line_busy', label: 'Ulaşılamadı / Hat Meşgul', color: PALETTE.bronze },
  { key: 'wrong_number', label: 'Yanlış Numara', color: PALETTE.red },
  { key: 'do_not_call', label: 'Aranmak İstemiyor', color: PALETTE.indigo },
  { key: 'no_call', label: 'Arama Yapılmadı', color: PALETTE.grey },
  // Added 2026-09-14 — grid positions 10-11 (engine STATUS_DEFS.tr twin; hex literal, not in PALETTE).
  { key: 'answered_by_other', label: 'Telefonu Başkası Açtı', color: '#2fb8a6' },
  { key: 'hung_up_mid_call', label: 'Konuşma Esnasında Kapattı', color: '#4db6e0' },
  // Legacy pre-2026-09-09 canons — historical reports (mirrors engine STATUS_DEFS.tr).
  { key: 'answered', label: 'Yanıtladı', color: PALETTE.green },
  { key: 'unreachable', label: 'Ulaşılamadı', color: PALETTE.amber },
  { key: 'busy', label: 'Meşgul', color: PALETTE.orange },
  { key: 'dissatisfied', label: 'Memnun Değil', color: PALETTE.pink },
  // Proxy-added result not yet in the engine's RAW_TO_CANON — neutral grey, always last.
  { key: 'other', label: 'Diğer (tanımsız)', color: '#8b8f98' },
];

const STATUS_EN: StatusDef[] = [
  { key: 'answered', label: 'Answered', color: PALETTE.green },
  { key: 'na', label: 'NA', color: PALETTE.amber },
  { key: 'call_back', label: 'Call Back', color: PALETTE.orange },
  { key: 'dissatisfied', label: 'Dissatisfied', color: PALETTE.pink },
  // Its own violet (extension grid parity) — never collides with Dissatisfied's pink.
  { key: 'not_interested', label: 'Not Interested', color: '#a07fe0' },
  { key: 'wrong_number', label: 'Wrong Number', color: PALETTE.red },
  { key: 'no_call', label: 'No Call', color: PALETTE.grey },
  { key: 'other', label: 'Other (undefined)', color: '#8b8f98' },
];

const statusSystemFor = (brandId: BrandId | string): StatusDef[] => (brandId === 'betbooker' ? STATUS_EN : STATUS_TR);
const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

// ── Oturum ───────────────────────────────────────────────────────────────────

/** Kullanıcı adı + şifre ile giriş. Başarısızsa null döner. */
export async function login(username: string, password: string): Promise<PanelUser | null> {
  try {
    return await http<PanelUser>('/api/auth/login', jsonInit('POST', { username, password }));
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 429)) return null;
    throw e;
  }
}

/** Sunucudaki oturumu doğrular (sayfa açılışında). 401 → null. */
export async function me(): Promise<PanelUser | null> {
  try {
    return await http<PanelUser>('/api/auth/me');
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return null;
    throw e;
  }
}

/** Sunucudaki oturumu kapatır (çerez temizlenir). Hata yutulur. */
export async function logout(): Promise<void> {
  try {
    await http<{ ok: boolean }>('/api/auth/logout', { method: 'POST' });
  } catch {
    /* oturum zaten düşmüş olabilir */
  }
}

// ── Statik yardımcılar (imzalar Phase 0 ile aynı) ────────────────────────────

/** Tüm markalar. */
export async function getBrands(): Promise<Brand[]> {
  return clone(BRANDS);
}

/** Bir kullanıcının erişebildiği markalar (rol bazlı). */
export async function getBrandsForUser(user: PanelUser | null): Promise<Brand[]> {
  const allowed = (user && user.brands) || [];
  return clone(BRANDS.filter((b) => allowed.includes(b.id)));
}

/** Departman listesi. */
export async function getDepartments(): Promise<string[]> {
  return clone(DEPARTMENTS);
}

/** Bir markanın durum sistemi (BetBooker => İngilizce, diğerleri => Türkçe). */
export async function getStatusSystem(brandId: BrandId): Promise<StatusDef[]> {
  return clone(statusSystemFor(brandId));
}

/** Her iki durum sistemi + palet (senkron kullanım için de faydalı). */
export function getStatusSystems(): StatusSystems {
  return { tr: clone(STATUS_TR), en: clone(STATUS_EN), palette: clone(PALETTE) };
}

// ── Canlı durum ──────────────────────────────────────────────────────────────

/** Canlı temsilci durumu (sunucu marka yetkisine göre süzer; seçili marka filtresi
 *  LiveStatus sayfasında istemci tarafında uygulanır — mock ile aynı). */
export async function getLiveAgents(): Promise<LiveAgent[]> {
  return http<LiveAgent[]>('/api/live');
}

/**
 * Canlı Durum itme kanalı (SSE, GET /api/live/stream). Her `live` olayı GET /api/live'ın AYNI
 * gövdesidir (sözleşme C9). Bağlantı yönetimi (geri çekilme, yoklamaya düşme) ÇAĞIRANDADIR: hata
 * olunca bu kanal KAPATILIR, tarayıcının kendi yeniden bağlanması kullanılmaz — kaç saniye sonra
 * yeniden deneneceğine sayfa karar verir. Dönen fonksiyon akışı kapatır.
 * EventSource yoksa (eski tarayıcı) onError hemen çağrılır → sayfa yoklamada kalır.
 */
export function openLiveStream(handlers: {
  onSnapshot: (rows: LiveAgent[]) => void;
  onError: () => void;
}): () => void {
  if (typeof EventSource === 'undefined') {
    setTimeout(handlers.onError, 0);
    return () => {};
  }
  const es = new EventSource(API_BASE + '/api/live/stream', { withCredentials: true });
  let closed = false;
  const close = () => {
    if (closed) return;
    closed = true;
    es.close();
  };
  es.addEventListener('live', (ev) => {
    if (closed) return;
    try {
      const rows = JSON.parse((ev as MessageEvent<string>).data) as LiveAgent[];
      if (Array.isArray(rows)) handlers.onSnapshot(rows);
    } catch {
      /* bozuk çerçeve — bir sonraki anlık görüntü düzeltir */
    }
  });
  es.onerror = () => {
    if (closed) return;
    close();
    handlers.onError();
  };
  return close;
}

// ── Vardiya planı ────────────────────────────────────────────────────────────

/** Bir güne ait vardiya planı ızgarası (marka kapsamına göre süzülür). Tarih verilmezse
 *  markanın YEREL bugünü (BetBooker = Akra) kullanılır. */
export async function getSchedule(date?: string, brands?: BrandId[]): Promise<ScheduleDay> {
  return http<ScheduleDay>('/api/schedule' + qs({ date, brands }));
}

/** Son senkron bilgisi (Ayarlar kartı) — admin. */
export async function getScheduleState(): Promise<ScheduleState> {
  return http<ScheduleState>('/api/schedule/state');
}

/** Planı Sheet'ten HEMEN yeniden oku (gün içi vardiya değişikliği) — admin.
 *  Sunucuda 60 sn'lik bir alt sınır var; çok sık çağrıda ok:false döner. */
export async function syncSchedule(): Promise<MutationResult<ScheduleState>> {
  return http<MutationResult<ScheduleState>>('/api/schedule/sync', { method: 'POST' });
}

// ── Raporlar ─────────────────────────────────────────────────────────────────

/** Dönem ön-seçimi verilirse SUNUCU aralığı marka gününe göre çözer (Vegas/Prime=
 *  İstanbul, BetBooker=Akra — karışık kapsamda marka başına ayrı "bugün"); start/end
 *  yalnız serbest (custom) aralıkta gider. range yine de etiket/CSV için hesaplanır. */
export async function getReport(
  range: DateRange,
  granularity: Granularity,
  brands: BrandId[],
  preset?: Exclude<ReportPreset, 'custom'>,
): Promise<AgentReport[]> {
  return http<AgentReport[]>(
    '/api/report' + qs(
      preset
        ? { preset, granularity, brands }
        : { start: range.start, end: range.end, granularity, brands },
    ),
  );
}

/** Ekip rakamları (döngü p50/p95 + havuzlanmış softphone ölçümü) — getReport ile AYNI aralık/kapsam. */
export async function getTeamReport(
  range: DateRange,
  brands: BrandId[],
  preset?: Exclude<ReportPreset, 'custom'>,
): Promise<TeamReport> {
  return http<TeamReport>(
    '/api/report/team' + qs(preset ? { preset, brands } : { start: range.start, end: range.end, brands }),
  );
}

// ── Haftalık Rapor ───────────────────────────────────────────────────────────

/**
 * Haftalık karne (Pzt 00:00 → Paz 23:59:59 İstanbul). `week` PAZARTESİ olmalıdır —
 * çağıran `weekStartOf` ile kıstırır, sunucu aksi halde 400 `not_monday` döner.
 * 26 haftadan eski istek 400 `week_too_old`; eşzamanlı SOĞUK hesap sınırı aşılırsa
 * 429 `busy` + `Retry-After` (ApiError.retryAfter).
 *
 * Veri SHEET taramasından gelir, bu app'in Postgres'inden DEĞİL — soğuk bir hafta
 * ~10 sn sürer, bu yüzden sayfa iskeleti veri gelene KADAR (ve gelen veri istenen
 * haftaya ait olana kadar) durur.
 */
export async function getWeeklyReport(week: string, brands: BrandId[]): Promise<WeeklyReportData> {
  return http<WeeklyReportData>('/api/weekly-report' + qs({ week, brands }));
}

// ── SMS ────────────────────────────────────────────────────────────────────────

/**
 * Giden SMS istatistikleri + günlüğü (SMS sayfası). Dönem ön-seçimi verilirse SUNUCU aralığı
 * marka gününe göre çözer (Vegas/Prime=İstanbul, BetBooker=Akra); start/end yalnız serbest
 * (custom) aralıkta gider. agentId/outcome/templateId opsiyonel süzgeçlerdir. İstatistikler
 * TÜM aralığı kapsar; günlük en yeni LOG_LIMIT satırla sınırlıdır (logTruncated bildirir).
 */
export async function getSmsReport(
  brands: BrandId[],
  opts: {
    preset?: Exclude<ReportPreset, 'custom'>;
    start?: string;
    end?: string;
    agentId?: string;
    outcome?: SmsOutcome;
    templateId?: string;
  } = {},
): Promise<SmsReport> {
  return http<SmsReport>(
    '/api/sms' +
      qs({
        brands,
        preset: opts.preset,
        start: opts.start,
        end: opts.end,
        agentId: opts.agentId,
        outcome: opts.outcome,
        templateId: opts.templateId,
      }),
  );
}

/**
 * WhatsApp sayfası — paylaşılan WhatsApp/Telegram dosyasının marka sekmeleri (GET /api/whatsapp).
 * Dönem SMS ile aynı kurallarla çözülür (preset marka gününe göre; start/end yalnız custom).
 * Süzgeç sunucuda yok: kayıtlar en yeni LOG_LIMIT satırla sınırlı, sayfa kendi içinde süzer.
 */
export async function getWhatsappReport(
  brands: BrandId[],
  opts: { preset?: Exclude<ReportPreset, 'custom'>; start?: string; end?: string } = {},
): Promise<WhatsappReport> {
  return http<WhatsappReport>('/api/whatsapp' + qs({ brands, preset: opts.preset, start: opts.start, end: opts.end }));
}

/**
 * Winback sayfası — bir BackOffice müşterisinin analizi (GET /api/winback). Yalnız izinli
 * kullanıcılar (sunucu allowlist'i, `user.features.winback`); diğerleri 403 alır. Ay
 * 'YYYY-MM' ya da 'all' (tüm zamanlar, görev #168); boşsa sunucu marka saatindeki bu ayı alır.
 */
export async function getWinback(brand: BrandId, customerId: string, month?: string): Promise<WinbackReport> {
  return http<WinbackReport>('/api/winback' + qs({ brand, id: customerId, month }));
}

/** Bütün Aramalar (görev #193/#194): bir müşteri ID'sine yapılan her çağrı, en yeniden eskiye. Marka kapsamı
 *  sunucuda (kapsam dışı marka 404). */
export async function getCustomerCalls(brand: BrandId, customerId: string): Promise<CustomerCallsResult> {
  return http<CustomerCallsResult>('/api/customer-calls' + qs({ brand, id: customerId }));
}

// ── Temsilci Detayı ──────────────────────────────────────────────────────────────

export async function getRoster(): Promise<RosterAgent[]> {
  return http<RosterAgent[]>('/api/roster');
}

export async function getAgentDay(agentId: string, date: string): Promise<AgentDayDetail> {
  return http<AgentDayDetail>(`/api/agents/${encodeURIComponent(agentId)}/day/${encodeURIComponent(date)}`);
}

/** Haftalık kart: `date`'i içeren Pzt–Paz İstanbul haftası. Sheet tarafı haftalık raporun
 *  cache'inden gelir; soğuk hafta tarama demektir (Raporlar sayfasıyla aynı kapı, 429 = meşgul). */
export async function getAgentWeek(agentId: string, date: string): Promise<AgentWeek> {
  return http<AgentWeek>(`/api/agents/${encodeURIComponent(agentId)}/week/${encodeURIComponent(date)}`);
}

// ── Liste Detayı (görev #222 + kopyaları, 2026-10-14) ───────────────────────────

/** Bir markanın listeleri — `date`'i içeren gün / Pzt–Paz haftası / takvim ayı. Sheet tarafı haftalık raporun
 *  cache'inden gelir (soğuk hafta = tarama); kapı doluysa o haftanın günleri boş döner ve `complete:false` olur. */
export async function getListDetail(brand: BrandId, period: ListDetailPeriod, date: string): Promise<ListDetailData> {
  return http<ListDetailData>('/api/list-detail' + qs({ brand, period, date }));
}

/** Bonus Kullanımı: bonus listesinde görüşülenlerden aramadan sonraki 48 saatte bonusu kullananlar. Karar panelin
 *  kendi işi (her çağrının 48. saatinde BackOffice okuması); kapsam dışı marka 404. */
export async function getBonusUsage(brand: BrandId, period: ListDetailPeriod, date: string): Promise<BonusUsageData> {
  return http<BonusUsageData>('/api/bonus-usage' + qs({ brand, period, date }));
}

// ── Molalar ──────────────────────────────────────────────────────────────────

export async function getBreaks(date: string, brands: BrandId[]): Promise<BreaksData> {
  return http<BreaksData>('/api/breaks' + qs({ date, brands }));
}

export async function grantBreakMinutes(agentId: string, minutes: number, reason: string): Promise<GrantResult> {
  return http<GrantResult>('/api/break-adjustments', jsonInit('POST', { agentId, minutes, reason }));
}

/** Admin force-close of a running break (PLAN req 5). `closed:false` = nothing was open. */
export async function closeBreak(agentId: string): Promise<{ agentId: string; closed: boolean }> {
  return http<{ agentId: string; closed: boolean }>(`/api/breaks/${encodeURIComponent(agentId)}/close`, jsonInit('POST', {}));
}

// ── Uzaktan destek komutları ─────────────────────────────────────────────────
// Komut, temsilcinin eklentisinin ZATEN yaptığı mola-durumu yoklamasına binerek gider
// (~30-60 sn). Panel bunu bekleyerek göstermez: satır 'pending' olarak eklenir, durumu
// getAgentCommands ile izlenir.

export async function issueAgentCommand(
  agentId: string, kind: string, args: Record<string, unknown> = {}, opts: { deferred?: boolean } = {},
): Promise<{ agentId: string; brandKey: string; tier: number; command: AgentCommand }> {
  // `deferred` = "bir sonraki açılışta uygula" (toolkit T2, yalnız 2.3.0+ eklentiye; sunucu TTL'yi 24 sa yapar).
  return http(`/api/agents/${encodeURIComponent(agentId)}/command`, jsonInit('POST', { kind, args, ...(opts.deferred ? { deferred: true } : {}) }));
}

export async function getAgentCommands(
  agentId: string,
): Promise<{ agentId: string; brandKey: string; commands: AgentCommand[] }> {
  return http(`/api/agents/${encodeURIComponent(agentId)}/commands`);
}

/** Yalnızca HENÜZ TESLİM ALINMAMIŞ bir komut iptal edilebilir. `cancelled:false` =
 *  temsilcinin tarayıcısı komutu çoktan almış; o an çalıştırıyor olabilir. */
export async function cancelAgentCommand(
  agentId: string, commandId: string,
): Promise<{ cancelled: boolean }> {
  return http(`/api/agents/${encodeURIComponent(agentId)}/command/${encodeURIComponent(commandId)}`, { method: 'DELETE' });
}

/** Eklenti Sağlığı çekmecesi: son durum raporu + günlük + komutlar + bugünün olayları (admin). */
export async function getExtHealth(agentId: string): Promise<ExtHealth> {
  return http<ExtHealth>(`/api/agents/${encodeURIComponent(agentId)}/ext-health`);
}

/** Eklenti filo sayfası: en çok görülen hatalar + rapor kutusu + ortam (admin). */
export async function getExtFleet(brands?: string[]): Promise<ExtFleet> {
  // The header's brand filter (the server intersects it with the caller's scope).
  const q = brands && brands.length ? '?brands=' + encodeURIComponent(brands.join(',')) : '';
  return http<ExtFleet>('/api/ext-fleet' + q);
}

/** Tek rapor (anlık görüntüsüyle). */
export async function getExtReport(id: string): Promise<ExtReport> {
  return http<ExtReport>(`/api/ext-reports/${encodeURIComponent(id)}`);
}

/** 2.5.0 — raporun yapıştırılmış ekran görüntüsü (JPEG); <img src> ve yeni sekme için. */
export function extReportImageUrl(id: string, idx: number): string {
  return `${API_BASE}/api/ext-reports/${encodeURIComponent(id)}/images/${idx}`;
}

export async function setExtReportStatus(id: string, status: ExtReportStatus): Promise<{ ok: boolean }> {
  return http(`/api/ext-reports/${encodeURIComponent(id)}`, jsonInit('PATCH', { status }));
}

// ── Uyarılar ─────────────────────────────────────────────────────────────────

export async function getAlerts(filter: AlertFilter = {}): Promise<Alert[]> {
  return http<Alert[]>(
    '/api/alerts' +
      qs({
        brands: filter.brands,
        ruleType: filter.ruleType,
        agentId: filter.agentId,
        brand: filter.brand,
        start: filter.start,
        end: filter.end,
        status: filter.status,
      }),
  );
}

/**
 * Kural tipi → başlık, CANLI eşiklerle (ör. longGapMin 15 iken "Uzun boşluk (>15 dk)").
 * Uyarı SATIRLARININ başlığı zaten /api/alerts'ten aynı kaynakla gelir; kural tipi
 * filtresi de bunu kullanır ki açılır liste satırlarla aynı eşiği yazsın. Başarısız
 * olursa çağıran constants.ts RULE_TITLES'a düşer (varsayılan eşikli metin).
 */
export async function getAlertRuleTitles(): Promise<AlertRuleTitles> {
  return http<AlertRuleTitles>('/api/alert-rules');
}

/** Kenar çubuğu rozeti için ucuz sayaç (tüm listeyi çekmez). */
export async function getNewAlertCount(brands: BrandId[]): Promise<number> {
  const r = await http<{ count: number }>('/api/alerts/count' + qs({ status: 'new', brands }));
  return r.count;
}

/** Onay açıklamasının üst sınırı — sunucu eşi src/api/alerts.js ACK_NOTE_MAX. */
export const ACK_NOTE_MAX = 500;

/** Onay = açıklama ZORUNLU (sahibin kuralı 2026-09-13): boş/blank `note` sunucuda 400
 *  `note_required` — istek hiç atılmaz, çağıran modal boşu zaten geçirmez. */
export async function ackAlert(id: string, note: string): Promise<Alert | null> {
  try {
    return await http<Alert>(`/api/alerts/${encodeURIComponent(id)}/ack`, jsonInit('POST', { note }));
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

/**
 * Toplu onay. Sunucu tek istekte en çok 500 id kabul eder (ack-batch MAX_BATCH_ACK);
 * "tümünü seç" 1000'e kadar id üretebildiği için burada parçalara böleriz ve
 * updated sayılarını toplarız. Parçalar sırayla gider (DB'yi yormamak için).
 * TEK açıklama seçimin tamamına yazılır — her parça aynı `note`'u taşır.
 */
const ACK_BATCH_CHUNK = 500;

export async function ackAlerts(ids: string[], note: string): Promise<{ updated: number }> {
  let updated = 0;
  for (let i = 0; i < ids.length; i += ACK_BATCH_CHUNK) {
    const chunk = ids.slice(i, i + ACK_BATCH_CHUNK);
    const res = await http<{ updated: number }>('/api/alerts/ack-batch', jsonInit('POST', { ids: chunk, note }));
    updated += res.updated;
  }
  return { updated };
}

// ── DARK ─────────────────────────────────────────────────────────────────────

export async function uploadDarkReport(file: File | null, date: string): Promise<DarkUpload> {
  if (!file) throw new ApiError(400, 'file_required');
  const fd = new FormData();
  fd.append('date', date);
  fd.append('file', file, file.name);
  // Content-Type elle verilmez — tarayıcı multipart boundary'yi kendisi yazar.
  return http<DarkUpload>('/api/dark/upload', { method: 'POST', body: fd });
}

export async function getDarkCompare(date: string, brands: BrandId[]): Promise<DarkCompare> {
  return http<DarkCompare>('/api/dark/compare' + qs({ date, brands }));
}

// ── Ayarlar: Kullanıcılar ────────────────────────────────────────────────────

export async function getUsers(): Promise<PanelUser[]> {
  return http<PanelUser[]>('/api/users');
}

export async function createUser(input: {
  username: string;
  password?: string;
  role?: Role;
  brands?: BrandId[];
  active?: boolean;
}): Promise<MutationResult> {
  return http<MutationResult>('/api/users', jsonInit('POST', input));
}

export async function updateUser(
  username: string,
  patch: { role?: Role; brands?: BrandId[]; active?: boolean },
): Promise<MutationResult> {
  return http<MutationResult>(`/api/users/${encodeURIComponent(username)}`, jsonInit('PATCH', patch));
}

export async function resetPassword(username: string, newPassword: string): Promise<MutationResult> {
  return http<MutationResult>(`/api/users/${encodeURIComponent(username)}/password`, jsonInit('POST', { newPassword }));
}

// ── Ayarlar: Panel Şifreleri (temsilcinin Call Agent paneli girişi — markanın proxy'sinde) ──

export async function getPanelPasswords(brand: BrandId): Promise<PanelPasswordList> {
  return http<PanelPasswordList>('/api/panel-passwords' + qs({ brand }));
}

export async function resetPanelPassword(brand: BrandId, agentName: string): Promise<PanelPasswordResult> {
  return http<PanelPasswordResult>(`/api/panel-passwords/${encodeURIComponent(brand)}/reset`, jsonInit('POST', { agentName }));
}

export async function setPanelPassword(brand: BrandId, agentName: string, newPassword: string): Promise<PanelPasswordResult> {
  return http<PanelPasswordResult>(
    `/api/panel-passwords/${encodeURIComponent(brand)}/set`,
    jsonInit('POST', { agentName, newPassword }),
  );
}

// Yeni temsilci: telefon listesi (proxy) + temsilci kaydı + panel girişi tek adımda. Santral
// doğrulaması ~10 sn sürebilir. Yalnız yönetici (sunucu admin:true).
export async function createNewHire(input: NewHireInput): Promise<NewHireResult> {
  return http<NewHireResult>('/api/new-hire', jsonInit('POST', input));
}

export async function updateDarkSecret(brand: BrandId, agentName: string, secret: string, verify: boolean): Promise<DarkSecretResult> {
  return http<DarkSecretResult>('/api/new-hire/dark-secret', jsonInit('POST', { brand, agentName, secret, verify }));
}

// ── Ayarlar: Temsilciler ─────────────────────────────────────────────────────────

export async function getAgentRecords(): Promise<AgentRecord[]> {
  return http<AgentRecord[]>('/api/agents');
}

export async function updateAgentRecord(
  id: string,
  patch: Partial<Pick<AgentRecord, 'name' | 'department' | 'brands' | 'active' | 'aliases' | 'temp' | 'teamLead'>>,
): Promise<MutationResult<AgentRecord>> {
  return http<MutationResult<AgentRecord>>(`/api/agents/${encodeURIComponent(id)}`, jsonInit('PATCH', patch));
}

export async function addAgentAlias(id: string, brand: BrandId, name: string): Promise<MutationResult<AgentRecord>> {
  return http<MutationResult<AgentRecord>>(`/api/agents/${encodeURIComponent(id)}/aliases`, jsonInit('POST', { brand, name }));
}

export async function removeAgentAlias(id: string, brand: BrandId, name: string): Promise<MutationResult<AgentRecord>> {
  return http<MutationResult<AgentRecord>>(
    `/api/agents/${encodeURIComponent(id)}/aliases` + qs({ brand, name }),
    { method: 'DELETE' },
  );
}

export async function mergeAgent(tempId: string, targetId: string): Promise<MutationResult<AgentRecord>> {
  return http<MutationResult<AgentRecord>>('/api/agents/merge', jsonInit('POST', { tempId, targetId }));
}

/** Panel hesabını markalarıyla eşitle (src/api/panel-accounts.js) — marka değişikliğinde zaten çalışır; bu, tekrar dener. */
export async function syncAgentPanel(id: string): Promise<{ ok: boolean; error?: string; panelSync?: PanelSyncResult }> {
  return http<{ ok: boolean; error?: string; panelSync?: PanelSyncResult }>(`/api/agents/${encodeURIComponent(id)}/panel-sync`, jsonInit('POST', {}));
}

/** Kaldır (migration 035): gizler + pasif yapar, geçmişi SİLMEZ. Geri alınabilir. */
export async function removeAgentRecord(id: string): Promise<MutationResult<AgentRecord>> {
  return http<MutationResult<AgentRecord>>(`/api/agents/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** İşten ayrıldı (src/api/offboard.js): ayrılış gününden itibaren kaldır + eklenti girişi + QMS, tek basış. */
export async function offboardAgentRecord(
  id: string,
  body: { leftOn: string; reason: OffboardReason; confirmName: string },
): Promise<OffboardResult> {
  return http<OffboardResult>(`/api/agents/${encodeURIComponent(id)}/offboard`, jsonInit('POST', body));
}

/** Kaldırmayı geri al — temsilci PASİF döner; aktifliği ayrı anahtar. */
export async function restoreAgentRecord(id: string): Promise<MutationResult<AgentRecord>> {
  return http<MutationResult<AgentRecord>>(`/api/agents/${encodeURIComponent(id)}/restore`, jsonInit('POST', {}));
}

// ── Ayarlar: Eşikler ─────────────────────────────────────────────────────────

export async function getThresholds(): Promise<Thresholds> {
  return http<Thresholds>('/api/thresholds');
}

export async function updateThresholds(patch: Partial<Thresholds>): Promise<Thresholds> {
  return http<Thresholds>('/api/thresholds', jsonInit('PATCH', patch));
}

// ── Ayarlar: Durum kategorileri (tembellik metriği) ──────────────────────────

export async function getLazinessConfig(): Promise<LazinessConfig> {
  return http<LazinessConfig>('/api/laziness');
}

export async function setLazinessIncluded(lang: Lang, key: StatusKey, included: boolean): Promise<{ ok: boolean }> {
  return http<{ ok: boolean }>('/api/laziness', jsonInit('PUT', { lang, key, included }));
}

// ── Varsayılan dışa aktarım ──────────────────────────────────────────────────
const CallAgentApi = {
  login,
  me,
  logout,
  getBrands,
  getBrandsForUser,
  getDepartments,
  getStatusSystem,
  getStatusSystems,
  getLiveAgents,
  openLiveStream,
  getSchedule,
  getScheduleState,
  syncSchedule,
  getReport,
  getTeamReport,
  getWeeklyReport,
  getSmsReport,
  getWhatsappReport,
  getWinback,
  getRoster,
  getAgentDay,
  getAgentWeek,
  getListDetail,
  getBonusUsage,
  getBreaks,
  grantBreakMinutes,
  closeBreak,
  getAlerts,
  getAlertRuleTitles,
  getNewAlertCount,
  ackAlert,
  ackAlerts,
  uploadDarkReport,
  getDarkCompare,
  getUsers,
  createUser,
  updateUser,
  resetPassword,
  getPanelPasswords,
  resetPanelPassword,
  setPanelPassword,
  createNewHire,
  updateDarkSecret,
  getAgentRecords,
  updateAgentRecord,
  addAgentAlias,
  removeAgentAlias,
  mergeAgent,
  removeAgentRecord,
  offboardAgentRecord,
  restoreAgentRecord,
  getThresholds,
  updateThresholds,
  getLazinessConfig,
  setLazinessIncluded,
};

export default CallAgentApi;
